import { type Dispatch, type FC, useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form-latest';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { _isNotEmpty, _notNil } from '@/littledash';
import { preventNumberScroll } from '@/helpers';
import { DateUtils } from '@/utils/Date.utils';
import { MAX_CAGE_POPULATION } from '@/constants/cage';
import type { CreateStudyAction, CreateStudyState } from '../../Create.model';
import type { AnimalSex } from '@/model/Animal.model';
import type { DraftFormData } from '@/model/Draft.model';
import type { State } from '@/model/State.model';

import { ErrorMessage } from '@hookform/error-message';
import Button from '@/components/UI/Button';
import { useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate.tsx';

import CageRow from './CageRow';
import { validationSchema } from './Cages.utils';

export interface CageFormEntry {
  name: string;
  dob: string;
  sex: AnimalSex;
  subjects: number;
}

interface CagesFormData {
  totalCages: number;
  collections: Array<CageFormEntry>;
}

interface CagesProps {
  state: CreateStudyState;
  dispatch: Dispatch<CreateStudyAction>;
}

const Cages: FC<CagesProps> = ({ state, dispatch }) => {
  const {
    register,
    getValues,
    setValue,
    clearErrors,
    reset,
    control,
    formState: { errors },
  } = useFormContext<CagesFormData>();
  const { setStepReady } = useStudyCreateStep();

  const { fields } = useFieldArray({
    control,
    name: 'collections',
  });
  const { features } = useSelector(({ team: { features } }: State) => ({
    features,
  }));

  const atunesIntegrationEnabled = features?.atunes_colony_management_integration === true;

  const generateCages = (num: number): Array<CageFormEntry> => {
    const result: Array<CageFormEntry> = [];
    for (let index = 0; index < num; index++) {
      result[index] = {
        name: `Cage ${index + 1}`,
        dob: DateUtils.dateNow(),
        sex: 'm',
        subjects: 5,
      };
    }

    return result;
  };

  useEffect(() => {
    setStepReady(true);
  }, [setStepReady]);

  const handleGenerate = () => {
    const totalCages = getValues('totalCages');
    const collections = generateCages(totalCages);
    reset({ collections });
    clearErrors('collections');
    setValue('collections', collections);
    dispatch({
      type: 'updateForm',
      data: { totalCages },
    });
    dispatch({
      type: 'updateStudy',
      data: { collections } as unknown as Partial<DraftFormData>,
    });
  };

  if (atunesIntegrationEnabled) {
    return <ATunesIntegrationEnabled />;
  }
  return (
    <>
      <div className="mb4">
        <h3 className="fw5 f5 pb2">Cages and animals</h3>
        <p>Skip this step if you are adding cages from Colony.</p>
      </div>
      <div className="mw7 ui-card" data-testid="cages-and-animals__container">
        <div className="pa4 bb b--moon-gray">
          <label htmlFor="totalCages">How many cages are there?</label>
          <p className="f6 mb2">Up to a maximum of {validationSchema.totalCages.max.value}</p>
          <div className="flex">
            <input
              id="totalCages"
              type="number"
              onWheel={preventNumberScroll}
              style={{ marginBottom: 0, width: '150px' }}
              className={cn('mr2', { input__error: _notNil(errors.totalCages) })}
              defaultValue={state.form.totalCages}
              data-testid="generate-cage-count-input"
              {...register('totalCages', {
                ...validationSchema.totalCages,
                valueAsNumber: true,
              })}
            />
            <Button plain disabled={_notNil(errors.totalCages)} onClick={handleGenerate} testId="generate-cages-button">
              Generate
            </Button>
          </div>
          <ErrorMessage
            errors={errors}
            name="totalCages"
            render={({ message }) => <p className="f6 red db pt2">{message}</p>}
          />
        </div>
        <div>
          <div className="pa4">
            <div className="flex justify-between flex-wrap items-bottom">
              <div>
                <label className={cn({ red: _notNil(errors.collections) })}>Cage contents</label>
                <p className="f6 measure-wide">Each cage can hold up to a maximum of {MAX_CAGE_POPULATION} animals</p>
              </div>
            </div>
          </div>
          <div className="bt b--moon-gray">
            {_isNotEmpty(fields) ? (
              <div>
                {fields.map((field, index) => (
                  <CageRow key={field.id} field={field} index={index} />
                ))}
              </div>
            ) : (
              <div style={{ height: 400 }} className="pa4">
                <div className="flex flex-wrap justify-center items-center br2 bg-near-white h-100 w-100 tc">
                  <div>
                    <h3 className="normal lh-title f4 near-black">Generate cages to get started</h3>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const ATunesIntegrationEnabled: FC = () => (
  <>
    <div className="mb4">
      <h3 className="fw5 f5 pb2">Cages and animals</h3>
      <p>Animals are managed through tick@lab</p>
    </div>
    <div className="mw7 ui-card">
      <div className="pa4 bb b--moon-gray">
        <p className="ma0">Add animals in tick@lab, then import them from the Cages page once your study is created.</p>
      </div>
    </div>
  </>
);

export default Cages;
